import moment from 'moment';
import { FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import check from '../../../assets/icons/check.svg';
import search from '../../../assets/icons/search.svg';
import { AnimatedDashboard } from '../../../components';
import BottomDrawer from '../../../components/common/BottomDrawer';
import { InputBackOffice } from '../../../components/common/InputBackoffice';
import Spinner from '../../../components/common/Spinner';
import { ErrorToast } from '../../../components/common/Toasts/Toasts';
import countries from '../../../lib/countries';
import { H2, H3, InputLabel, P1, P2, P3 } from '../../../styles/FontStyles';
import { ArrowPolygon, Button, Select } from '../../../styles/GlobalComponents';
import {
  FlexCenter,
  SInput,
  SInputContainer
} from '../../../styles/stylesStyled';
import { useWindowWide } from '../../../helpers/wideScreen';
import { Desktop, Mobile } from '../KycStyled';
import { addKycPersonalDetails } from './KycPersonalDetails.actios';

const KycPersonalDetails = ({ history }: RouteComponentProps) => {
  const { t } = useTranslation('kyc');
  const [isVisible3, setIsVisible3] = useState(false);
  const [countryList, setCountryList] = useState(countries);
  const [countryImage, setCountryImage] = useState('');
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [country, setCountry] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [userCountry, setUserCountry] = useState('');

  const onClose3 = useCallback(() => {
    setIsVisible3(false);
  }, []);

  const dispatch = useAppDispatch();
  const { userInfo } = useAppSelector((state) => state.auth);
  const { loading } = useAppSelector((state) => state.kyc);
  const { user } = userInfo;

  const checkCountry = (country: any) => {
    setCountryList(
      countryList.map((item) =>
        item.name === country.name
          ? {
              ...item,
              checked: true
            }
          : {
              ...item,
              checked: false
            }
      )
    );
  };

  useEffect(() => {
    fetch(`https://api.ipregistry.co/?key=${process.env.REACT_APP_IP_REGISTRY}`)
      .then(function (response) {
        return response.json();
      })
      .then(function (payload) {
        setUserCountry(payload?.location?.country?.name);
      });
  }, [dispatch]);

  const onHandleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!country) {
      ErrorToast(t('personal_details.err_please_add_country'));
    } else if (!name) {
      ErrorToast(t('personal_details.err_please_add_name'));
    } else if (!surname) {
      ErrorToast(t('personal_details.err_please_add_last_name'));
    } else if (!day) {
      ErrorToast(t('personal_details.err_please_add_day_of_birth'));
    } else if (!month || month.length < 1) {
      ErrorToast(t('personal_details.err_please_add_month_of_birth'));
    } else if (!year || year.length < 4) {
      ErrorToast(t('personal_details.err_please_add_year_of_birth'));
    } else {
      dispatch(
        addKycPersonalDetails({
          day,
          month,
          year,
          country,
          name,
          surname,
          countryCode,
          history
        })
      );
    }
  };

  useEffect(() => {
    if (user?.name) {
      setCountry(user.country);
      setCountryCode(user.countryCode);
    }
  }, [dispatch, user]);

  const handleChangeDay = (e: any) => {
    if (e <= 31 && e.length <= 2) {
      setDay(e);
    }
  };

  const handleChangeMonth = (e: any) => {
    if (e <= 12 && e.length <= 2) {
      setMonth(e);
    }
  };

  const handleChangeYear = (e: any) => {
    const currentYear = moment().format('YYYY');

    if (e <= Number(currentYear) - 18 && e.length <= 4) {
      setYear(e);
    }
  };

  const wide = useWindowWide(1000);

  return (
    <AnimatedDashboard key={'kyc-personal-details'}>
      <div style={{ width: '100%', maxWidth: wide ? 333 : '' }}>
        {loading && <Spinner fullScreen={true} transparent />}

        <Desktop>
          <H2 mb='12'>{t('personal_details.title')}</H2>

          <P1 mb='40' maxWidth='600px' maxWidthSM='1000px'>
            {t('personal_details.subtitle')}
          </P1>
        </Desktop>
        <Mobile>
          <H3 mb='8' center>
            {t('personal_details.title')}
          </H3>

          <P2 mb='32' center>
            {t('personal_details.subtitle')}
          </P2>
        </Mobile>

        <form onSubmit={onHandleSubmit}>
          <InputLabel>{t('personal_details.fullname_label')}</InputLabel>

          <InputBackOffice
            id={'name'}
            type={'text'}
            placeholder={t('personal_details.name_placeholder')}
            name={'name'}
            value={name ? name.replace(/[^a-zA-Z šŠĐđČčćŽž ]/g, '') : ''}
            onChange={(e: string) => {
              setName(
                e
                  .replace(/[^a-zA-Z šŠĐđČčćŽž ]/g, '')
                  .charAt(0)
                  .toUpperCase() + e.slice(1).toLowerCase()
              );
            }}
            style={{ textTransform: 'capitalize' }}
          />

          <InputBackOffice
            id={'lastname'}
            type={'text'}
            placeholder={t('personal_details.lastname_placeholder')}
            name={'lastname'}
            value={surname ? surname.replace(/[^a-zA-ZšŠĐđČčćŽž ]/g, '') : ''}
            onChange={(e: string) => {
              setSurname(
                e
                  .replace(/[^a-zA-ZšŠĐđČčćŽž ]/g, '')
                  .charAt(0)
                  .toUpperCase() + e.slice(1).toLowerCase()
              );
            }}
            label={'text'}
          />

          <InputLabel>{t('personal_details.birth_date_label')}</InputLabel>

          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ width: 'calc(20% - 4px)' }}>
              <InputBackOffice
                id={'day'}
                type={'number'}
                placeholder={t('personal_details.day_placeholder')}
                name={'day'}
                value={day}
                min='1'
                max='31'
                onChange={(e: number) => {
                  handleChangeDay(e);
                }}
                label={'Day'}
                padding={5}
                inputStyle={{ textAlign: 'center' }}
              />
            </div>
            <div style={{ width: 'calc(20% - 8px)' }}>
              <InputBackOffice
                id={'month'}
                type={'number'}
                placeholder={t('personal_details.month_placeholder')}
                name={'month'}
                value={month}
                onChange={(e: string) => {
                  handleChangeMonth(e);
                }}
                label={'Month'}
                padding={5}
                inputStyle={{ textAlign: 'center' }}
              />
            </div>
            <div style={{ width: 'calc(60% - 4px)' }}>
              <InputBackOffice
                id={'year'}
                type={'number'}
                placeholder={t('personal_details.year_placeholder')}
                name={'year'}
                value={year}
                onChange={(e: string) => {
                  handleChangeYear(e);
                }}
                label={'Year'}
                inputStyle={{ textAlign: 'center' }}
              />
            </div>
          </div>

          <div>
            <InputLabel>{t('personal_details.country_label')}</InputLabel>

            <Select
              onClick={() => {
                setIsVisible3(true);
              }}
              style={{ cursor: 'pointer' }}>
              <FlexCenter>
                {countryImage && (
                  <img
                    src={countryImage}
                    alt='flag'
                    style={{
                      marginRight: '20px',
                      width: '28px',
                      height: '20px'
                    }}
                  />
                )}

                <div>
                  {country || t('personal_details.country_placeholder')}
                </div>
              </FlexCenter>
              <ArrowPolygon direction={''} />
            </Select>
          </div>

          <Desktop>
            <Button maxWidth='200px' type='submit'>
              {t('personal_details.continue')}
            </Button>
          </Desktop>
          <Mobile>
            <Button type='submit'>{t('personal_details.continue')}</Button>
          </Mobile>
        </form>
        <BottomDrawer isVisible={isVisible3} onClose={onClose3} height={'90vh'}>
          <SInputContainer>
            <img src={search} alt='search.svg' />
            <SInput
              placeholder={t('personal_details.search_placeholder')}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SInputContainer>
          {countryList &&
            countryList
              ?.sort((a) => (a.name === userCountry ? -1 : 1))
              .filter((value) => {
                if (searchTerm === '') {
                  return value;
                } else if (
                  value.name.toLowerCase().includes(searchTerm.toLowerCase())
                ) {
                  return value;
                } else return null;
              })
              .map((country, i) => {
                if (country.hidden) return null;
                return (
                  <div
                    onClick={() => {
                      setCountry(country.name);
                      setCountryCode(country.code);
                      setCountryImage(country.flag);
                      setIsVisible3(false);
                      checkCountry(country);
                    }}
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer'
                    }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '20px'
                      }}>
                      <img
                        src={country.flag}
                        alt='flag'
                        style={{
                          marginRight: '20px',
                          width: '28px',
                          height: '20px'
                        }}
                      />

                      <P3>{country.name}</P3>
                    </div>
                    {country.checked && (
                      <img
                        src={check}
                        alt='check'
                        style={{ marginBottom: '30px' }}
                      />
                    )}
                  </div>
                );
              })}
        </BottomDrawer>
      </div>
    </AnimatedDashboard>
  );
};

export default KycPersonalDetails;
